import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from '@pharvio/accounts/admin-account/data-access';
import { BranchesState } from '@pharvio/branches/data-access';
import {
  AuthUser,
  convertToMajor,
  DateAdapterService,
  hasPermission,
  IBranchSuperintendentStatusRow,
  IOrderStatsDailyRow,
  isLoaded,
  OfflineModeService
} from '@pharvio/core/shared';
import {
  AmountToMajorPipe,
  ChartComponent,
  ChartData,
  LayoutToolbarComponent,
  PricePipe,
  SkeletonRowComponent
} from '@pharvio/core/ui';
import { CurrencyState } from '@pharvio/currency/data-access';
import {
  LoadCustomerStats,
  LoadDashboard,
  LoadExpiringSoon,
  LoadLowStock,
  LoadOrderStats,
  LoadOutOfStock,
  LoadShifts,
  LoadSuperintendentStatus,
  LoadTopProducts,
  LoadUsersExpiringCredentials,
  SetSelectedBranches,
  TenantDashboardState
} from '@pharvio/dashboard/data-access';
import {
  ExpensesState,
  LoadExpenseSummary
} from '@pharvio/expenses/data-access';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { PERMISSIONS } from '../../../../core/constants/permissions.constant';
import { AdminPaths } from '../../../../core/constants/routes/admin-routes';
import { CustomersPaths } from '../../../../core/constants/routes/customers-routes';
import { InventoryPaths } from '../../../../core/constants/routes/inventory-routes';
import { OrderPaths } from '../../../../core/constants/routes/orders-routes';
import { ReportPaths } from '../../../../core/constants/routes/reports-routes';
import { UserPaths } from '../../../../core/constants/routes/users-routes';
import { TenantContextService } from '../../../../core/services/tenant-context.service';
import { BranchSelectorComponent } from '../../../../shared/components/branch-selector/branch-selector.component';
import { ExpensesMonthCardComponent } from '../../../expenses/components/expenses-month-card/expenses-month-card.component';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { KpiCardComponent } from '../../components/kpi-card/kpi-card.component';
import { ListCardComponent } from '../../components/list-card/list-card.component';
import { ShiftCardComponent } from '../../components/shift-card/shift-card.component';

interface TrendInfo {
  change: number;
  changePercent: number;
  isPositive: boolean;
}

@Component({
  selector: 'app-dashboard',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    DatePipe,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    LayoutToolbarComponent,
    SkeletonRowComponent,
    PricePipe,
    AmountToMajorPipe,
    ChartComponent,
    HasPermissionDirective,
    KpiCardComponent,
    ListCardComponent,
    ShiftCardComponent,
    BranchSelectorComponent,
    ExpensesMonthCardComponent
  ],
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly dateService = inject(DateAdapterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly offlineMode = inject(OfflineModeService);
  private readonly tenant = inject(TenantContextService);

  /** Offline: widgets come from the device cache and cannot be refreshed. */
  readonly isOffline = this.offlineMode.isOffline;
  readonly lastLoadedAt$ = this.store.select(TenantDashboardState.lastLoadedAt);
  readonly fromCache$ = this.store.select(TenantDashboardState.fromCache);

  readonly PERMS = PERMISSIONS;
  readonly orderPaths = OrderPaths;
  readonly inventoryPaths = InventoryPaths;
  readonly customersPaths = CustomersPaths;
  readonly reportPaths = ReportPaths;
  readonly adminPaths = AdminPaths;
  readonly userPaths = UserPaths;

  readonly user$: Observable<AuthUser | null> = this.store.select(
    AuthState.currentUser
  );
  readonly currency$ = this.store.select(CurrencyState.currency);
  readonly selectedBranchIds$: Observable<string[]> = this.store.select(
    TenantDashboardState.selectedBranchIds
  );

  /**
   * The expenses summary takes one `branchId`, but this page's selector is
   * multi-select. Only an unambiguous single choice is passed through; any
   * other selection means "everything", which is what omitting it does.
   */
  private readonly selectedBranchIds = this.store.selectSignal(
    TenantDashboardState.selectedBranchIds
  );
  readonly soleSelectedBranchId = computed(() => {
    const ids = this.selectedBranchIds();
    return ids.length === 1 ? ids[0] : null;
  });

  // ─── Expenses this month ───

  private readonly permissions = this.store.selectSignal(
    AuthState.permissions
  );
  /** Gates both the dispatch below and the card in the template. */
  readonly canReadExpenses = computed(() =>
    hasPermission(this.permissions(), [PERMISSIONS.EXPENSES.READ])
  );

  readonly expenseSummary = this.store.selectSignal(ExpensesState.summary);

  /** The month's range, so the card's link lands on the same figures. */
  readonly expenseMonthQuery = computed<Record<string, string>>(() => {
    const range = this.tenant.currentMonthRange();
    const branchId = this.soleSelectedBranchId();
    return {
      from: range.from,
      to: range.to,
      ...(branchId ? { branchId } : {})
    };
  });

  /**
   * The summary is its own state slice, so `LoadDashboard` never touches it.
   * This effect is what keeps it in step with the branch selector, and
   * `refresh()` calls `loadExpenseSummary` directly.
   *
   * Reading `canReadExpenses()` here also means nothing is dispatched until
   * permissions have actually resolved — no speculative 403 on a cold load.
   */
  private readonly loadExpensesOnBranchChange = effect(() => {
    this.soleSelectedBranchId();
    if (!this.canReadExpenses()) return;
    this.loadExpenseSummary();
  });

  private loadExpenseSummary(): void {
    const branchId = this.soleSelectedBranchId();
    this.store.dispatch(
      new LoadExpenseSummary({
        month: this.tenant.currentMonth(),
        ...(branchId ? { branchId } : {})
      })
    );
  }

  // Single order stats (14-day range — feeds all order widgets + charts + trends)
  readonly orderStats$ = this.store.select(TenantDashboardState.orderStats);

  // Other widget observables
  readonly openShifts$ = this.store.select(TenantDashboardState.openShifts);

  readonly lowStock$ = this.store.select(TenantDashboardState.lowStock);
  readonly expiringSoon$ = this.store.select(TenantDashboardState.expiringSoon);
  readonly outOfStock$ = this.store.select(TenantDashboardState.outOfStock);
  readonly topProducts$ = this.store.select(TenantDashboardState.topProducts);
  readonly superintendentStatus$ = this.store.select(
    TenantDashboardState.superintendentStatus
  );
  readonly usersExpiringCredentials$ = this.store.select(
    TenantDashboardState.usersExpiringCredentials
  );
  readonly customerStats$ = this.store.select(
    TenantDashboardState.customerStats
  );

  // Derived: superintendent filters
  readonly unsupervisedBranches$: Observable<IBranchSuperintendentStatusRow[]> =
    this.superintendentStatus$.pipe(
      map((item) => (item.data ?? []).filter((b) => b.isUnsupervised))
    );
  readonly expiredCredentialBranches$: Observable<
    IBranchSuperintendentStatusRow[]
  > = this.superintendentStatus$.pipe(
    map((item) => (item.data ?? []).filter((b) => b.hasExpiredCredential))
  );

  // ─── Derived from single orderStats (14-day dailyBreakdown) ───

  /** Today's row from the 14-day breakdown */
  private readonly todayRow$: Observable<IOrderStatsDailyRow | null> =
    this.orderStats$.pipe(
      map((item) => {
        const rows = item.data?.dailyBreakdown ?? [];
        return rows.length > 0 ? rows[rows.length - 1] : null;
      })
    );

  /** Last 7 rows (this week) */
  private readonly thisWeekRows$: Observable<IOrderStatsDailyRow[]> =
    this.orderStats$.pipe(
      map((item) => {
        const rows = item.data?.dailyBreakdown ?? [];
        return rows.slice(-7);
      })
    );

  // Derived KPI values
  readonly todayOrderCount$: Observable<number> = this.todayRow$.pipe(
    map((r) => r?.orderCount ?? 0)
  );
  readonly todayRevenue$: Observable<number> = this.todayRow$.pipe(
    map((r) => r?.grossSalesMinor ?? 0)
  );
  readonly pendingOrders$: Observable<number> = this.orderStats$.pipe(
    map((item) => item.data?.pendingOrders ?? 0)
  );
  readonly weekOrderCount$: Observable<number> = this.thisWeekRows$.pipe(
    map((rows) => rows.reduce((sum, r) => sum + r.orderCount, 0))
  );

  /**
   * The window `weekOrderCount$` covers, as orders-list query params, so the
   * card links to exactly the orders it counted.
   */
  readonly weekRange$: Observable<{ from: string; to: string } | null> =
    this.thisWeekRows$.pipe(
      map((rows) => {
        if (rows.length === 0) return null;
        return {
          from:
            this.dateService.dateToString(
              this.dateService.fromIsoString(rows[0].date)
            ) ?? '',
          to:
            this.dateService.dateToString(
              this.dateService.fromIsoString(rows[rows.length - 1].date)
            ) ?? ''
        };
      })
    );
  // AmountMajor applied on Template
  readonly weekRevenue$: Observable<number> = this.thisWeekRows$.pipe(
    map((rows) => rows.reduce((sum, r) => sum + r.netSalesMinor, 0))
  );

  // Trends (today vs yesterday, this week vs last week)
  readonly ordersTodayTrend$: Observable<TrendInfo | null> =
    this.orderStats$.pipe(
      map((item) => {
        const rows = item.data?.dailyBreakdown ?? [];
        if (rows.length < 2) return null;
        return this.computeTrend(
          rows[rows.length - 1].orderCount,
          rows[rows.length - 2].orderCount
        );
      })
    );
  readonly revenueTodayTrend$: Observable<TrendInfo | null> =
    this.orderStats$.pipe(
      map((item) => {
        const rows = item.data?.dailyBreakdown ?? [];
        if (rows.length < 2) return null;
        return this.computeTrend(
          rows[rows.length - 1].grossSalesMinor,
          rows[rows.length - 2].grossSalesMinor
        );
      })
    );
  readonly ordersWeekTrend$: Observable<TrendInfo | null> =
    this.orderStats$.pipe(
      map((item) => {
        const rows = item.data?.dailyBreakdown ?? [];
        if (rows.length < 8) return null;
        const thisWeek = rows.slice(-7);
        const prevWeek = rows.slice(0, rows.length - 7);
        const curr = thisWeek.reduce((s, r) => s + r.orderCount, 0);
        const prev = prevWeek.reduce((s, r) => s + r.orderCount, 0);
        return this.computeTrend(curr, prev);
      })
    );
  // Derived customer KPIs from 14-day dailyBreakdown
  readonly newCustomersThisWeek$: Observable<number> = this.customerStats$.pipe(
    map((item) => {
      const rows = item.data?.dailyBreakdown ?? [];
      return rows.slice(-7).reduce((sum, r) => sum + r.newCustomers, 0);
    })
  );
  readonly newCustomersTrend$: Observable<TrendInfo | null> =
    this.customerStats$.pipe(
      map((item) => {
        const rows = item.data?.dailyBreakdown ?? [];
        if (rows.length < 8) return null;
        const thisWeek = rows.slice(-7).reduce((s, r) => s + r.newCustomers, 0);
        const prevWeek = rows
          .slice(0, rows.length - 7)
          .reduce((s, r) => s + r.newCustomers, 0);
        return this.computeTrend(thisWeek, prevWeek);
      })
    );

  // ─── Charts (all from single orderStats response) ───

  readonly paymentMethodDonut$: Observable<ChartData | null> =
    this.orderStats$.pipe(
      map((item) => {
        const breakdown = item.data?.paymentMethodBreakdown;
        if (!breakdown) return null;
        const entries = Object.entries(breakdown).filter(
          ([, v]) => v.totalMinor > 0
        );
        if (entries.length === 0) return null;
        return {
          type: 'donut' as const,
          categories: entries.map(([k]) => k.replace(/_/g, ' ')),
          series: [
            {
              label: 'Revenue',
              data: entries.map(([, v]) => convertToMajor<number>(v.totalMinor))
            }
          ],
          valueFormat: 'currency' as const,
          showTable: true,
          showGrid: false
        };
      })
    );

  readonly orderSourceDonut$: Observable<ChartData | null> =
    this.orderStats$.pipe(
      map((item) => {
        const breakdown = item.data?.orderSourceBreakdown;
        if (!breakdown) return null;
        const entries = Object.entries(breakdown).filter(([, v]) => v > 0);
        if (entries.length === 0) return null;
        return {
          type: 'donut' as const,
          categories: entries.map(([k]) => k.replace(/_/g, ' ')),
          series: [{ label: 'Orders', data: entries.map(([, v]) => v) }],
          showTable: true,
          showGrid: false
        };
      })
    );

  readonly ordersBarChart$: Observable<ChartData | null> =
    this.thisWeekRows$.pipe(
      map((rows) => {
        if (rows.length === 0) return null;
        return {
          type: 'bar' as const,
          categories: rows.map((r) => r.date.slice(5)),
          series: [{ label: 'Orders', data: rows.map((r) => r.orderCount) }],
          showLegend: false,
          showGrid: true
        };
      })
    );

  readonly revenueAreaChart$: Observable<ChartData | null> =
    this.thisWeekRows$.pipe(
      withLatestFrom(this.currency$),
      map(([rows, currency]) => {
        if (rows.length === 0) return null;
        return {
          type: 'area' as const,
          categories: rows.map((r) => r.date.slice(5)),
          series: [
            {
              label: 'Net Sales',
              data: rows.map((r) => convertToMajor<number>(r.netSalesMinor))
            }
          ],
          valueFormat: 'currency' as const,
          showLegend: false,
          showGrid: false,
          currency: currency?.code
        };
      })
    );

  readonly newCustomersChart$: Observable<ChartData | null> =
    this.customerStats$.pipe(
      map((item) => {
        const rows = (item.data?.dailyBreakdown ?? []).slice(-7);
        if (rows.length === 0) return null;
        return {
          type: 'bar' as const,
          categories: rows.map((r) => r.date.slice(5)),
          series: [
            { label: 'New Customers', data: rows.map((r) => r.newCustomers) }
          ],
          showLegend: false,
          showGrid: true
        };
      })
    );

  readonly topProductColumns = ['rank', 'product', 'qty', 'revenue'];
  protected readonly isLoaded = isLoaded;

  ngOnInit(): void {
    // Wait for auth to be fully loaded (user + branches available)
    // before dispatching dashboard API calls.
    this.user$
      .pipe(
        filter((user) => user !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const branches = this.store.selectSnapshot(
          BranchesState.activeBranches
        );
        if (branches.length > 0) {
          this.store.dispatch(
            new SetSelectedBranches(branches.map((b) => b.id))
          );
        }
        this.store.dispatch(new LoadDashboard());
      });
  }

  refresh(): void {
    if (this.isOffline()) return;
    this.store.dispatch(new LoadDashboard());
    // Its own state slice, so `LoadDashboard` doesn't cover it.
    if (this.canReadExpenses()) this.loadExpenseSummary();
  }

  onBranchChange(branchIds: string[]): void {
    this.store.dispatch(new SetSelectedBranches(branchIds));
  }

  trendColorClass(trend: TrendInfo | null, invert = false): string {
    if (!trend || trend.change === 0) return 'text-secondary';
    const good = invert ? !trend.isPositive : trend.isPositive;
    return good ? 'text-green-600' : 'text-red-600';
  }

  // Retry handlers
  retryExpenses(): void {
    this.loadExpenseSummary();
  }
  retryOrderStats(): void {
    this.store.dispatch(new LoadOrderStats());
  }
  retryOpenShifts(): void {
    this.store.dispatch(new LoadShifts());
  }
  retryLowStock(): void {
    this.store.dispatch(new LoadLowStock());
  }
  retryExpiringSoon(): void {
    this.store.dispatch(new LoadExpiringSoon());
  }
  retryOutOfStock(): void {
    this.store.dispatch(new LoadOutOfStock());
  }
  retryTopProducts(): void {
    this.store.dispatch(new LoadTopProducts());
  }
  retrySuperintendentStatus(): void {
    this.store.dispatch(new LoadSuperintendentStatus());
  }
  retryUsersExpiring(): void {
    this.store.dispatch(new LoadUsersExpiringCredentials());
  }
  retryCustomerStats(): void {
    this.store.dispatch(new LoadCustomerStats());
  }

  private computeTrend(
    current: number | undefined,
    previous: number | undefined
  ): TrendInfo | null {
    if (current === undefined || previous === undefined) return null;
    const change = current - previous;
    const changePercent =
      previous === 0
        ? current > 0
          ? 100
          : 0
        : Math.round((change / previous) * 100 * 100) / 100;
    return { change, changePercent, isPositive: change >= 0 };
  }
}
